import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Checkbox } from './ui/Checkbox';
import { AVAILABLE_MEMBERS } from '../data/mockData';
import { OwnershipRecord, ValidationError } from '../types/ownership';
interface OwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (record: Omit<OwnershipRecord, 'id' | 'createdDate'>) => {
    success: boolean;
    errors: ValidationError[];
  };
  initialData?: OwnershipRecord;
  entityId: string;
}
export function OwnershipModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  entityId
}: OwnershipModalProps) {
  const [formData, setFormData] = useState({
    memberId: '',
    ownershipPercentage: '',
    effectiveStartDate: new Date().toISOString().split('T')[0],
    effectiveEndDate: '',
    isPrimaryMember: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          memberId: initialData.memberId,
          ownershipPercentage: initialData.ownershipPercentage.toString(),
          effectiveStartDate: initialData.effectiveStartDate,
          effectiveEndDate: initialData.effectiveEndDate || '',
          isPrimaryMember: initialData.isPrimaryMember
        });
      } else {
        // Reset for new record
        setFormData({
          memberId: '',
          ownershipPercentage: '',
          effectiveStartDate: new Date().toISOString().split('T')[0],
          effectiveEndDate: '',
          isPrimaryMember: false
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);
  const handleSubmit = () => {
    // Basic form validation before calling parent
    const newErrors: Record<string, string> = {};
    if (!formData.memberId) newErrors.memberId = 'Please select a member';
    if (!formData.ownershipPercentage) newErrors.ownershipPercentage = 'Required';
    if (!formData.effectiveStartDate) newErrors.effectiveStartDate = 'Required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const member = AVAILABLE_MEMBERS.find(m => m.id === formData.memberId);
    const recordToSave = {
      memberId: formData.memberId,
      memberName: member?.name || 'Unknown',
      entityId: entityId,
      ownershipPercentage: Number(formData.ownershipPercentage),
      effectiveStartDate: formData.effectiveStartDate,
      effectiveEndDate: formData.effectiveEndDate || undefined,
      isPrimaryMember: formData.isPrimaryMember
    };
    const result = onSave(recordToSave);
    if (result.success) {
      onClose();
    } else {
      // Map validation errors to field errors
      const serverErrors: Record<string, string> = {};
      result.errors.forEach(err => {
        serverErrors[err.field] = err.message;
      });
      setErrors(serverErrors);
    }
  };
  const memberOptions = AVAILABLE_MEMBERS.map(m => ({
    label: `${m.name} (${m.recordType})`,
    value: m.id
  }));
  return <Modal isOpen={isOpen} onClose={onClose} title={initialData ? 'Edit Ownership' : 'New Ownership'} footer={<>
          <Button variant="neutral" onClick={onClose} data-id="element-115">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} data-id="element-116">
            Save
          </Button>
        </>} data-id="element-114">
      <div className="space-y-4" data-id="element-117">
        <Select label="Member" required options={memberOptions} value={formData.memberId} onChange={e => setFormData({
        ...formData,
        memberId: e.target.value
      })} error={errors.memberId} placeholder="Select a member..." disabled={!!initialData} // Cannot change member on edit, usually
      data-id="element-118" />

        <div className="grid grid-cols-2 gap-4" data-id="element-119">
          <Input label="Ownership %" type="number" required min="0" max="100" value={formData.ownershipPercentage} onChange={e => setFormData({
          ...formData,
          ownershipPercentage: e.target.value
        })} error={errors.ownershipPercentage} data-id="element-120" />

          <div className="flex items-end pb-2" data-id="element-121">
            <Checkbox label="Primary Member" checked={formData.isPrimaryMember} onChange={e => setFormData({
            ...formData,
            isPrimaryMember: e.target.checked
          })} data-id="element-122" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-123">
          <Input label="Effective Start Date" type="date" required value={formData.effectiveStartDate} onChange={e => setFormData({
          ...formData,
          effectiveStartDate: e.target.value
        })} error={errors.effectiveStartDate} data-id="element-124" />

          <Input label="Effective End Date" type="date" helperText="Leave blank for open-ended" value={formData.effectiveEndDate} onChange={e => setFormData({
          ...formData,
          effectiveEndDate: e.target.value
        })} error={errors.effectiveEndDate} data-id="element-125" />
        </div>

        {errors.general && <div className="p-3 bg-red-50 text-[#ba0517] text-sm rounded border border-red-100" data-id="element-126">
            {errors.general}
          </div>}
      </div>
    </Modal>;
}